import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatIDR, formatDate, formatNumber } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Printer, Scissors } from "lucide-react";

const qr = (data) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=0&data=${encodeURIComponent(data)}`;

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={strong ? "font-semibold text-slate-900" : "text-slate-800 text-right"}>{value}</span>
  </div>
);

export default function PrintDoc({ open, onOpenChange, order, mode = "spk" }) {
  const [biz, setBiz] = useState({ business_name: "BordirKom Workshop", phone: "", address: "", logo_url: "" });
  useEffect(() => {
    if (open) api.get("/settings").then((r) => setBiz(r.data)).catch(() => {});
  }, [open]);
  if (!order) return null;
  const isSPK = mode === "spk";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
        <div className="flex items-center justify-between border-b px-5 py-3 no-print">
          <h3 className="font-heading font-bold text-slate-900">
            {isSPK ? "Pratinjau SPK (Surat Perintah Kerja)" : "Pratinjau Nota / Invoice"}
          </h3>
          <Button data-testid="btn-do-print" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" /> Cetak
          </Button>
        </div>

        <div className="print-area p-8 bg-white text-slate-900">
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-slate-900 text-white">
                {biz.logo_url ? <img src={biz.logo_url} alt="logo" className="h-full w-full object-contain" /> : <Scissors className="h-6 w-6" />}
              </div>
              <div>
                <p className="font-heading text-xl font-extrabold">{biz.business_name}</p>
                <p className="text-xs text-slate-500">{[biz.address, biz.phone].filter(Boolean).join(" · ")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading text-lg font-extrabold uppercase">{isSPK ? "SPK" : "NOTA"}</p>
              <p className="font-mono text-sm">{order.order_id}</p>
              <p className="text-xs text-slate-500">{formatDate(order.created_at)}</p>
            </div>
          </div>

          {/* Customer + QR */}
          <div className="mt-5 flex justify-between gap-6">
            <div className="space-y-0.5 text-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Pelanggan</p>
              <p className="font-semibold">{order.customer_name}</p>
              {order.company && <p className="text-slate-600">{order.company}</p>}
              {order.phone && <p className="text-slate-600">{order.phone}</p>}
            </div>
            <div className="text-center">
              <img src={qr(order.order_id)} alt="QR" className="h-24 w-24" crossOrigin="anonymous" />
              <p className="mt-1 font-mono text-[10px] tracking-widest">{order.order_id}</p>
            </div>
          </div>

          {isSPK ? (
            <>
              {/* Job details */}
              <div className="mt-5 grid grid-cols-2 gap-x-8 rounded-lg border border-slate-200 p-4">
                <Row label="Jenis Garment" value={order.garment_type || "-"} />
                <Row label="Posisi Bordir" value={order.placement || "-"} />
                <Row label="Nama Desain" value={order.design_name || "-"} />
                <Row label="Jumlah Stitch" value={`${formatNumber(order.stitch_count)} tusukan`} />
                <Row label="Qty" value={`${formatNumber(order.qty)} pcs`} />
                <Row label="Mesin" value={order.machine_id || "-"} />
                <Row label="Target Selesai" value={formatDate(order.target_date)} />
                <Row label="Warna Benang" value={(order.thread_colors || []).join(", ") || "-"} />
              </div>
              {order.notes && <p className="mt-3 text-sm"><span className="font-semibold">Catatan: </span>{order.notes}</p>}

              <div className="mt-4 flex items-center gap-3">
                <span className="text-sm font-semibold">Status Produksi:</span>
                <StatusBadge value={order.status} />
              </div>

              {/* Production checklist */}
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Tahapan Produksi</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {["Design & Punching","Antrean Mesin","Proses Bordir","Finishing & Trimming","Siap Ambil","Selesai"].map((s) => (
                    <div key={s} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1.5">
                      <span className="inline-block h-3.5 w-3.5 rounded-sm border border-slate-400" /> {s}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Invoice table */}
              <table className="mt-5 w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-300 text-left text-slate-500">
                    <th className="py-2">Deskripsi</th>
                    <th className="py-2 text-center">Qty</th>
                    <th className="py-2 text-right">Harga</th>
                    <th className="py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-2">
                      Bordir {order.design_name} ({formatNumber(order.stitch_count)} stitch @ Rp {formatNumber(order.rate_per_1000)}/1000)
                      <div className="text-xs text-slate-500">{order.garment_type} · {order.placement}</div>
                    </td>
                    <td className="py-2 text-center">{order.qty}</td>
                    <td className="py-2 text-right">{formatIDR((order.stitch_count/1000)*order.rate_per_1000)}</td>
                    <td className="py-2 text-right">{formatIDR((order.stitch_count/1000)*order.rate_per_1000*order.qty)}</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2" colSpan={3}>Biaya Setup / Film / Punching</td>
                    <td className="py-2 text-right">{formatIDR(order.setup_fee)}</td>
                  </tr>
                  {order.addons_fee > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-2" colSpan={3}>Biaya Tambahan (Metallic / Patch / Rush)</td>
                      <td className="py-2 text-right">{formatIDR(order.addons_fee)}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="mt-4 ml-auto w-full max-w-xs">
                <Row label="Grand Total" value={formatIDR(order.total)} strong />
                <Row label="DP Dibayar" value={formatIDR(order.dp)} />
                <div className="my-1 border-t border-dashed border-slate-300" />
                <Row label="Sisa Tagihan" value={formatIDR(order.remaining)} strong />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Status</span>
                  <StatusBadge value={order.payment_status} kind="payment" />
                </div>
              </div>
            </>
          )}

          <div className="mt-8 flex justify-between text-xs text-slate-500">
            <div>
              <p className="mb-8">Hormat Kami,</p>
              <p className="border-t border-slate-400 pt-1 w-40">{biz.business_name}</p>
            </div>
            <div className="text-right">
              <p className="mb-8">Pelanggan,</p>
              <p className="border-t border-slate-400 pt-1 w-40">{order.customer_name}</p>
            </div>
          </div>
          <p className="mt-6 text-center text-[11px] text-slate-400">Terima kasih telah mempercayakan bordir Anda kepada kami 🧵</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
